import { XZ } from '../sim/centerline';
import { Checkpoint, Level, Placement } from '../sim/level';

export type TimingEvent =
  | { type: 'checkpoint'; index: number; time: number }
  | { type: 'lap'; lap: number; time: number; total: number }
  | { type: 'finished'; total: number; laps: number[] }
  | { type: 'missed'; expected: number; crossed: number; respawn: Placement };

/**
 * Times a run through a level's checkpoints, in order. Checkpoint 0 is start/finish: crossing it after all the
 * others ends a lap. A gate is the line through the checkpoint square to its heading, `gateHalfWidth` each side;
 * only forward crossings count, at the interpolated crossing time. Crossing a later checkpoint than the expected
 * one reports `missed` with the last passed checkpoint as the respawn point; the game moves the rider there.
 */
export class CheckpointTimer {
  next: number;
  laps: number[] = [];
  done = false;
  private lapStart: number;

  constructor(readonly checkpoints: readonly Checkpoint[], readonly lapCount: number, readonly gateHalfWidth: number[],
    first = 1, startTime = 0) {
    if (checkpoints.length < 2) throw new Error('timing needs at least two checkpoints');
    this.next = first % checkpoints.length;
    this.lapStart = startTime;
  }

  /** Timer for a level from a named start: the first expected checkpoint is the first one ahead of the start. */
  static forLevel(level: Level, startName: string, laps: number, startTime = 0): CheckpointTimer {
    const line = level.line;
    const start = level.starts[startName];
    if (!line || !start) throw new Error('timing needs a track level and a known start');
    const s = line.closest(start.x, start.z).s;
    const i = level.checkpoints.findIndex((c) => c.s > s);
    const corridor = level.def.forest?.corridor ?? 0;
    const widths = level.checkpoints.map((c) => Math.max(c.halfWidth + 2, corridor));
    return new CheckpointTimer(level.checkpoints, laps, widths, i < 0 ? 0 : i, startTime);
  }

  /** Adds `seconds` to the lap being ridden (a fall's penalty: the lap goes on, it is not restarted). */
  penalize(seconds: number): void { this.lapStart -= seconds; }

  get bestLap(): number | null { return this.laps.length ? Math.min(...this.laps) : null; }

  /** Call once per step with the rider position before and after the step. */
  update(prev: XZ, pos: XZ, time: number, dt: number): TimingEvent[] {
    if (this.done) return [];
    const events: TimingEvent[] = [];
    const n = this.checkpoints.length;
    for (let i = 0; i < n; i++) {
      const c = this.checkpoints[i]!;
      const fx = Math.sin(c.heading), fz = Math.cos(c.heading);
      const a = (prev.x - c.x) * fx + (prev.z - c.z) * fz;
      const b = (pos.x - c.x) * fx + (pos.z - c.z) * fz;
      if (!(a < 0 && b >= 0)) continue;
      const u = -a / (b - a);
      const ix = prev.x + (pos.x - prev.x) * u, iz = prev.z + (pos.z - prev.z) * u;
      const lateral = Math.abs((ix - c.x) * fz - (iz - c.z) * fx);
      if (lateral > this.gateHalfWidth[i]!) continue;
      const t = time - dt + dt * u;
      // The gate just passed, crossed again (put back a little before it after a fall): nothing to time or miss.
      if (i === (this.next - 1 + n) % n) continue;
      if (i !== this.next) {
        const last = this.checkpoints[(this.next - 1 + n) % n]!;
        events.push({ type: 'missed', expected: this.next, crossed: i, respawn: { x: last.x, z: last.z, heading: last.heading } });
        continue;
      }
      events.push({ type: 'checkpoint', index: i, time: t });
      this.next = (i + 1) % n;
      if (i !== 0) continue;
      const lapTime = t - this.lapStart;
      this.lapStart = t;
      this.laps.push(lapTime);
      events.push({ type: 'lap', lap: this.laps.length, time: lapTime, total: t });
      if (this.laps.length >= this.lapCount) {
        this.done = true;
        events.push({ type: 'finished', total: this.laps.reduce((x, y) => x + y, 0), laps: [...this.laps] });
        break;
      }
    }
    return events;
  }
}
